import { useTranslation } from 'react-i18next'

import {
  AccordionRoot,
  type BadgeProps,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { Badge } from '~/components/common/Badge'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import type {
  ColumnGuideEntry,
  ColumnGuideRequirement,
} from '~/models/columnGuide'

// chakra v3.29 + React 18 types 既知不具合ワークアラウンド
// (SupplyCapacityChange/index.tsx・SpotPrice/index.tsx 同形)。
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

/**
 * 「必須」区分 → semantic status バッジの写像。相対取引契約・供給地点マスタの
 * 両 data type で完全に同一の文言 (`common:upload.columnGuide.requirement*`)
 * のため component 内部の定数として持つ。`EnumStatusBadge`
 * は proto enum (`E extends number`) 向けの component で `ColumnGuideRequirement`
 * (文字列 union) をそのまま渡せないため、ここでは経由せず `Badge` を直接描く
 * (数値キーへの二重写像を作らない)。
 */
const REQUIREMENT_BADGE: Record<
  ColumnGuideRequirement,
  { status: BadgeProps['status']; labelKey: string }
> = {
  required: {
    status: 'negative',
    labelKey: 'common:upload.columnGuide.requirementRequired',
  },
  conditional: {
    status: 'warning',
    labelKey: 'common:upload.columnGuide.requirementConditional',
  },
  optional: {
    status: 'neutral',
    labelKey: 'common:upload.columnGuide.requirementOptional',
  },
}

type Props = {
  rows: ColumnGuideEntry[]
  /**
   * accordion の見出し。 省略時は共通の「CSV 入力ルール」。 1 画面に複数の表を
   * 並べる時 (ブロック入札 CSV の 3 表) に種別名で区別するために上書きする。
   */
  title?: string
  /**
   * 列数メタに出す CSV の実列数。 省略時は表の行数。 1 行に複数の実列を集約する表
   * (48点データの「コマ1〜コマ48」) は行数が実列数と合わないので、 実列数を渡す。
   */
  columnCount?: number
}

/**
 * `columns` 定義を component 本体の外に置く。component 本体の中で毎レンダー
 * 新規に配列リテラルを作ると `react/no-unstable-nested-components` が
 * (JSX を返す arrow function を「render 中に定義された component」と誤検知
 * して) 発火するため、`t` を引数で渡して module scope の関数のまま保つ。
 *
 * 5 列すべての見出しラベルを `common:upload.columnGuide.*` から解決する。
 * `supply-point-master.md:150` / `bilateral-trade.md:317,321` はいずれも
 * 「列名 / 必須 / 説明 / 入力形式 / 記入例」で完全一致しており、両 data type で
 * 異なる文言を持つ列は無い (issue #6467 レビュー指摘: 相対取引側が独自に
 * 持っていた「形式・値域」は spec §2 の内部列名を画面に流用した pre-existing の
 * 逸脱であり、共有 component の必須 prop として温存すべきではなかった。本 PR で
 * spec 準拠の「入力形式」に統一して解消した)。
 */
function buildColumns(
  t: (key: string) => string,
): Array<MasterDataTableColumn<ColumnGuideEntry>> {
  return [
    {
      key: 'name',
      header: t('common:upload.columnGuide.columnName'),
      cell: (row) => row.name,
    },
    {
      key: 'required',
      header: t('common:upload.columnGuide.required'),
      cell: (row) => {
        const spec = REQUIREMENT_BADGE[row.required]
        return <Badge status={spec.status}>{t(spec.labelKey)}</Badge>
      },
    },
    {
      key: 'description',
      header: t('common:upload.columnGuide.description'),
      cell: (row) => row.description,
    },
    {
      key: 'rule',
      header: t('common:upload.columnGuide.rule'),
      cell: (row) => row.rule,
    },
    {
      key: 'example',
      header: t('common:upload.columnGuide.example'),
      cell: (row) => row.example,
    },
  ]
}

/**
 * CSV アップロード画面 Step1 (ファイル選択) パネル内の記入ガイド表
 * (列名 / 必須区分 / 説明 / 入力形式 / 記入例。common.md AC-DUC-037)。
 * 雛形ダウンロードと同一パネルに表示する data type (相対取引契約 /
 * 供給地点マスタ) が共用する。
 *
 * design SoT (`design/admin/uploads/cis-contract-master.html:70-72`、
 * `design/admin/uploads/bilateral-trade.html:91`。いずれも 2026-08-26 更新の
 * design backup が最終版) は「記入ガイドは折りたたみのままなので、
 * 再アップロード時は下の dropzone まで素通りできる」という明示の理由で
 * **既定閉じの accordion** にしている。本 component もそれに従う
 * (issue #6467 レビュー指摘: 常時展開はこの意図を打ち消す)。
 */
export const ColumnGuideTable = ({
  rows,
  title,
  columnCount,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const columns = buildColumns(t)

  return (
    <AccordionRoot collapsible defaultValue={[]}>
      <AccordionItem
        overflow="hidden"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor={LightModeSemanticColors.Surface.primary.value}
        value="guide"
      >
        {/* padding: design `css/components.css:4159-4162` の
            `.ecl-accordion-trigger { padding: 8px 12px; }` (縦 8px / 横 12px)。 */}
        <AccordionItemTrigger paddingX={3} paddingY={2}>
          {/* 開閉標は header の**左端**。 DS `AccordionIconButton` を使う */}
          <AccordionDisclosureIcon />
          <Text textStyle="sectionTitle">
            {title ?? t('common:upload.columnGuide.title')}
          </Text>
          {/* トリガー右端のメタ情報 (件数)。design `css/components.css:4171-4187`
              `.ecl-accordion-meta` は `margin-left: auto` で**トリガーの右端**へ
              寄せる (= 見出し直後にインラインで置く形ではない)。
              `AccordionItemTrigger` は DS の accordion slot recipe で
              `display: flex` (chakra `accordion.recipe.ts` の `itemTrigger`) の
              ため、この Text を見出しの兄弟として置けば auto margin が効く。 */}
          <Text
            as="span"
            textStyle="accordionMeta"
            marginLeft="auto"
            color="Text.midEmphasis"
            whiteSpace="nowrap"
          >
            {t('common:upload.columnGuide.columnCount', {
              count: columnCount ?? rows.length,
            })}
          </Text>
        </AccordionItemTrigger>
        {/* padding: 0。design `css/components.css:4205` の汎用
            `.ecl-accordion-content { padding: var(--space-4); }` (16px) は本文向けで、
            この「記入ガイド」表 (design `js/upload-spec.js` が生成する
            `.up-spec-accordion` / summary文言「CSV入力ルール」と同一パターン) には
            より詳細度の高い専用 override `css/components.css:5313-5315`
            `.up-spec-accordion-content { padding: 0; }` が効く
            (「表は端まで見せたいので content 自体は 0 にし」)。 border-top のみ
            汎用ルールのまま (5313-5315 側に border-top の打ち消しは無い)。 */}
        <AccordionItemContent
          padding={0}
          borderTopWidth="1px"
          borderTopColor="Border.lowEmphasis"
        >
          {/* [DS差異] `MasterDataTable` は共通部品で、単体利用を既定に自前の
              外枠 border/radius を持つ (= 他画面の一覧表としては正しい既定)。
              design `css/components.css:5319` の
              `.up-spec-accordion-content .up-spec-table-wrap { border: none;
              border-radius: 0; }` は「accordion に収めた記入ガイド表は accordion
              自身の外枠だけを見せ、表自体の枠は消す」と決めており、この
              accordion 内に限り既定を打ち消す。`MasterDataTable` 本体は変更せず
              (共通部品の interface 変更は影響範囲が広いため。枠なし表示を
              `MasterDataTable` 自身の prop/variant にするのが本来の形だが、
              それは別 issue — issue #6467 レビュー指摘)、ここでは nested
              selector で直接の子 div の border だけを打ち消す。

              この override は次の 3 条件が `MasterDataTable` の実装詳細として
              成立している間だけ効く (公開 interface の契約ではないため、
              `MasterDataTable` を無言で壊さずに変えられる余地がある。型にも
              既存テストにも現れない暗黙の結合):
                (a) ルートが `div` にレンダリングされる chakra `Box` であること
                (b) その直下の子が `div` 1 つだけであること (2 個以上や別要素に
                    なると `& > div` が意図と違う要素に当たる、または不一致で
                    無言で効かなくなる)
                (c) border/border-radius がそのルートノード自身に載っている
                    こと (子孫要素に付け替わると届かない) */}
          <Box
            css={{
              '& > div': {
                border: 'none',
                borderRadius: 0,
              },
            }}
          >
            <MasterDataTable
              columns={columns}
              getRowKey={(row) => row.name}
              rows={rows}
            />
          </Box>
        </AccordionItemContent>
      </AccordionItem>
    </AccordionRoot>
  )
}
